import type { ComponentType, ReactNode, SVGProps } from 'react';
import type { Service } from '@bekebi/contracts/api';
import { BucketIcon, CloudIcon, GlobeIcon, MailIcon } from '../../components/icons';
import {
  HEALTH_LABELS,
  HEALTH_REASON_TEXT,
  formatDate,
  formatDateTime,
  formatPrice,
  renewalStatusText,
} from './format';

/**
 * Shared service presentation. Health is shown exactly as the API derived it — state, reason and
 * the time of the newest check — with a text label and a distinct symbol, never colour alone.
 */
const HEALTH_STYLE: Record<Service['health']['state'], { symbol: string; className: string }> = {
  healthy: { symbol: '✓', className: 'border-success-line bg-success-soft text-success' },
  needs_attention: { symbol: '!', className: 'border-warning-line bg-warning-soft text-warning' },
  unknown: { symbol: '?', className: 'border-unknown-line bg-unknown-soft text-unknown' },
};

export function HealthBadge({ health }: { health: Service['health'] }) {
  const style = HEALTH_STYLE[health.state];
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${style.className}`}
    >
      <span aria-hidden="true" className="text-[0.7rem] leading-none">
        {style.symbol}
      </span>
      <span>
        <span className="sr-only">Health: </span>
        {HEALTH_LABELS[health.state]}
      </span>
    </span>
  );
}

const TYPE_ICONS: Record<Service['type'], ComponentType<SVGProps<SVGSVGElement>>> = {
  cloud_server: CloudIcon,
  mail_server: MailIcon,
  storage_bucket: BucketIcon,
  website: GlobeIcon,
};

/** The service's type, as a recognisable icon tile (decorative: the name is always beside it). */
export function ServiceTypeIcon({
  type,
  size = 'md',
}: {
  type: Service['type'];
  size?: 'md' | 'lg';
}) {
  const TypeIcon = TYPE_ICONS[type];
  const box = size === 'lg' ? 'size-12 rounded-2xl' : 'size-10 rounded-xl';
  return (
    <span
      className={`flex ${box} shrink-0 items-center justify-center bg-info-soft text-accent-link`}
    >
      <TypeIcon className={size === 'lg' ? 'size-6' : 'size-5'} />
    </span>
  );
}

/** "Last checked" as the API reports it; says so plainly when there has been no check. */
export function LastChecked({ checkedAt }: { checkedAt: string | null }) {
  return checkedAt ? <>{formatDateTime(checkedAt)}</> : <>No check result yet</>;
}

export function HealthSummary({ health }: { health: Service['health'] }) {
  return (
    <div className="space-y-2">
      <HealthBadge health={health} />
      <p className="text-sm text-fg-2">{HEALTH_REASON_TEXT[health.reason]}</p>
      <p className="text-sm text-muted">
        Last checked: <LastChecked checkedAt={health.checkedAt} />
      </p>
    </div>
  );
}

/** The current renewal term summary from the service response, or an honest "none" state. */
export function RenewalSummary({ renewal }: { renewal: Service['renewal'] }) {
  if (!renewal) return <p className="text-sm text-muted">No open renewal term is recorded.</p>;
  return (
    <DefinitionList
      items={[
        ['Renewal date', formatDate(renewal.renewalDate)],
        ['Condition', renewalStatusText(renewal.condition, renewal.countdown)],
        ['Price', formatPrice(renewal.price)],
      ]}
    />
  );
}

export function DefinitionList({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[max-content_1fr]">
      {items.map(([term, value]) => (
        <div key={term} className="contents">
          <dt className="font-medium text-muted">{term}</dt>
          <dd className="text-fg">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Section({
  title,
  description,
  children,
  action,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  const id = `section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <section
      aria-labelledby={id}
      className="rounded-[var(--radius-card)] border border-line bg-surface p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h2 id={id}>{title}</h2>
          {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
        </div>
        {action ? <div className="shrink-0 text-sm">{action}</div> : null}
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

/** A clearly-labelled "not available / not connected" note — never a placeholder value. */
export function UnavailableNote({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-lg border border-dashed border-line-strong px-4 py-3 text-sm text-muted">
      {children}
    </p>
  );
}
